import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { loadMessages, locale } from 'devextreme/localization';
import * as itMessages from 'devextreme/localization/messages/it.json';
import { environment } from 'environment';
import { KeycloakService } from 'keycloak-angular';

/**
 * Returns the first candidate that is one of the Transloco available languages, or the default language.
 * The Keycloak `locale` attribute is free text in the user profile: a value like 'ILMA' makes
 * Intl (and therefore every DevExtreme grid) throw a RangeError.
 */
export function resolveSupportedLang(translocoService: TranslocoService, ...candidates: (string | null | undefined)[]): string {
    const availableLangs = translocoService.getAvailableLangs().map((lang) => (typeof lang === 'string' ? lang : lang.id));
    const supported = candidates
        .map((candidate) => candidate?.trim().toLowerCase())
        .find((candidate) => !!candidate && availableLangs.includes(candidate));

    return supported ?? translocoService.getDefaultLang();
}

@Injectable({
    providedIn: 'root',
})
export class LanguageInitializationService {
    constructor(
        private readonly keycloakService: KeycloakService,
        private readonly translocoService: TranslocoService
    ) {}

    /**
     * Initialize language from Keycloak token or fallback to default
     * This method mimics the pattern used in AuthGuard for language initialization
     */
    async initializeLanguageFromKeycloak(): Promise<void> {
        try {
            // Get locale from keycloak (same pattern as AuthGuard line 54)
            const keycloakInstance = this.keycloakService.getKeycloakInstance();
            const localeLang = resolveSupportedLang(this.translocoService, keycloakInstance?.idTokenParsed?.locale);

            // Save locale in localStorage (same pattern as AuthGuard lines 55-60)
            localStorage.setItem(environment.localStorageVariables.locale, localeLang);

            // Save the extended locale in localStorage like it-IT
            // Get full locale code from simple locale like 'it' -> it-IT, en -> en-US
            const extendedLocale = `${localeLang}-${localeLang.toUpperCase()}`;
            localStorage.setItem(environment.localStorageVariables.extendedLocale, extendedLocale);

            if (localeLang) {
                // Set active language in Transloco
                this.translocoService.setActiveLang(localeLang);

                // Configure DevExtreme locale for consistency
                locale(localeLang);
                if (localeLang === 'it') {
                    loadMessages(itMessages);
                }

                // Load translations and handle errors with fallback
                await new Promise<void>((resolve, reject) => {
                    this.translocoService.load(localeLang).subscribe({
                        next: () => {
                            console.log(`Language initialized successfully: ${localeLang} (${extendedLocale})`);
                            resolve();
                        },
                        error: (err) => {
                            console.error(`Error loading language ${localeLang}:`, err);
                            // Fallback to default language
                            this.initializeFallbackLanguage();
                            reject(err);
                        },
                    });
                });
            } else {
                console.warn('LocaleLang not found in idTokenParsed. Using default language.');
                this.initializeFallbackLanguage();
            }
        } catch (error) {
            console.error('Error during language initialization:', error);
            // Silently fallback to default language
            this.initializeFallbackLanguage();
        }
    }

    /**
     * Initialize fallback language (default language)
     */
    private initializeFallbackLanguage(): void {
        const defaultLang = this.translocoService.getDefaultLang();

        // Configure DevExtreme locale
        locale(defaultLang);
        if (defaultLang === 'it') {
            loadMessages(itMessages);
        }

        // Set active language
        this.translocoService.setActiveLang(defaultLang);

        console.log(`Fallback language set to: ${defaultLang}`);
    }

    /**
     * Get current language from localStorage or fallback to default
     */
    getCurrentLanguage(): string {
        return localStorage.getItem(environment.localStorageVariables.locale) || this.translocoService.getDefaultLang();
    }

    /**
     * Check if language is already initialized
     */
    isLanguageInitialized(): boolean {
        return !!localStorage.getItem(environment.localStorageVariables.locale);
    }

    /**
     * Switch to a specific language
     */
    async switchLanguage(requestedLang: string): Promise<void> {
        const lang = resolveSupportedLang(this.translocoService, requestedLang);
        localStorage.setItem(environment.localStorageVariables.locale, lang);
        const extendedLocale = `${lang}-${lang.toUpperCase()}`;
        localStorage.setItem(environment.localStorageVariables.extendedLocale, extendedLocale);

        this.translocoService.setActiveLang(lang);
        locale(lang);
        if (lang === 'it') {
            loadMessages(itMessages);
        }

        await new Promise<void>((resolve) => {
            this.translocoService.load(lang).subscribe({ next: () => resolve(), error: () => resolve() });
        });
    }
}
